import * as THREE from 'three';
import { emis, smoke, std } from '../materials.ts';
import type { Rng } from '../rand.ts';
import { M, V, glowSprite } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Polilla del sueño (docs/CRIATURAS.md §2, era III): a great moth out of a fever dream, drawn to light. They come
 * in flocks (one model is one moth; the game instances the swarm), dim the lanterns and beacons they cluster on
 * and put to sleep the hero they wrap in their wings. Its wings are its silhouette from the table camera: broad,
 * soft, pale ash and bone banded with dusk and bruised violet-blue, a checker of dark scales on the scalloped
 * margins, torn here and there, and on each hindwing an eye that glows faintly with cold dream light. A shaggy
 * ash collar, a furred and ringed abdomen, plumed antennae, ink-black eyes with a pale shine.
 *
 * Cheap for the swarm: the wings are fans of faces painted in bands (plain colours the baker folds into one
 * draw), one translucent material (the fringe of loose scales on the wing margins) and one self-lit one (the
 * eyes of the hindwings, the eyeshine and the sleep-dust), plus glow sprites.
 *
 * It flies: the hover is its rest pose (`MOTH_HOVER`; the clips beat the wings and bob it, `die` drops it). At
 * rest the wings are spread flat. Bones: body; head (antennae on it); abdomen; foreL/hindL, foreR/hindR (the
 * forewing and the hindwing of each side); legs; and `dust`, a cloud of glowing motes and haze around the body
 * that the clips swirl and swell (×2.5 when it sheds its sleep-dust). The right wings are +X: on them +Z lifts
 * the wing, +Y sweeps it back, +X turns the leading edge down (the left ones mirror). +X on body and head
 * pitches them nose down; +X on the abdomen curls it down; +X on the antennae lowers them; +X swings the legs back.
 */

/** Height of the model's origin over the ground while it flies (the clips drop it from here to die). */
export const MOTH_HOVER = 0.66;
/** The body's height over the model's origin. */
const Y0 = 0.36;

type P2 = readonly [number, number];

/** A wing: a fan of faces from its root to a scalloped outline, painted face by face in bands. */
interface WingDef {
  root: P2;
  y: number;
  /** The outline (x, z for the right wing): from beside the root along the leading edge, round, back beside the root. */
  ctrl: P2[];
  S: number;
  RN: number;
  /** Outline params [from, to] of the outer margin (scalloped and fringed). */
  margin: readonly [number, number];
  /** Spokes torn deeper. */
  tears: number[];
  droop: number;
  /** Colour of the face in ring `i` (0 = at the root) between spokes j and j+1 (`u` its param along the outline). */
  paint: (i: number, j: number, u: number) => string;
}

interface BuiltWing {
  /** Vertex at ring i (1..RN), spoke j: position. */
  at: (i: number, j: number) => THREE.Vector3;
  /** The surface height at a point (droop from the root). */
  yAt: (x: number, z: number) => number;
}

const wingMat = (c: string): THREE.MeshStandardMaterial => std(c, { roughness: 0.92, side: THREE.DoubleSide, bumpScale: 0.035 });

/** Builds one wing (`k` = 1 right, −1 left) under `parent`: a mesh per colour on shared vertices. */
const buildWing = (d: WingDef, k: number, parent: THREE.Object3D, rng: Rng, fringeM: THREE.Material): BuiltWing => {
  const curve = new THREE.CatmullRomCurve3(d.ctrl.map(([x, z]) => V(x, 0, z)), false, 'centripetal');
  const [rx, rz] = d.root;
  const outline: THREE.Vector3[] = [];
  const isMargin = (u: number): boolean => u > d.margin[0] && u < d.margin[1];
  let lmax = 0;
  for (let j = 0; j <= d.S; j++) {
    const u = j / d.S;
    const p = curve.getPointAt(u);
    if (isMargin(u)) {
      // Scalloped between the veins, and torn deeper here and there.
      const pull = (j % 2 === 1 ? 0.05 + rng() * 0.035 : 0) + (d.tears.includes(j) ? 0.13 : 0);
      p.set(rx + (p.x - rx) * (1 - pull), 0, rz + (p.z - rz) * (1 - pull));
    }
    outline.push(p);
    lmax = Math.max(lmax, Math.hypot(p.x - rx, p.z - rz));
  }
  const yAt = (x: number, z: number): number => {
    const r = Math.hypot(x - rx, z - rz) / lmax;
    // Bellied a little near the body, drooping to the tips.
    return d.y + 0.012 * Math.sin(Math.min(1, r) * Math.PI) - d.droop * r * r;
  };
  const pos: number[] = [];
  const vtx = (x: number, z: number): number => {
    pos.push(x * k, yAt(x, z), z);
    return pos.length / 3 - 1;
  };
  const root = vtx(rx, rz);
  const grid: number[][] = [];
  for (let i = 1; i <= d.RN; i++) {
    const row: number[] = [];
    for (let j = 0; j <= d.S; j++) {
      const p = outline[j]!;
      const f = i / d.RN;
      row.push(vtx(rx + (p.x - rx) * f, rz + (p.z - rz) * f));
    }
    grid.push(row);
  }
  const bins = new Map<string, number[]>();
  const face = (c: string, a: number, b: number, e: number): void => {
    const l = bins.get(c) ?? [];
    l.push(a, b, e);
    bins.set(c, l);
  };
  for (let j = 0; j < d.S; j++) {
    const u = (j + 0.5) / d.S;
    face(d.paint(0, j, u), root, grid[0]![j]!, grid[0]![j + 1]!);
    for (let i = 1; i < d.RN; i++) {
      const a0 = grid[i - 1]![j]!, a1 = grid[i - 1]![j + 1]!, b0 = grid[i]![j]!, b1 = grid[i]![j + 1]!;
      const c = d.paint(i, j, u);
      face(c, a0, b0, b1);
      face(c, a0, b1, a1);
    }
  }
  const arr = new Float32Array(pos);
  // Both wings face up alike: wind every face so its normal points up.
  const first = [...bins.values()][0]!;
  const p = (i: number): THREE.Vector3 => V(arr[i * 3]!, arr[i * 3 + 1]!, arr[i * 3 + 2]!);
  const n = p(first[1]!).sub(p(first[0]!)).cross(p(first[2]!).sub(p(first[0]!)));
  const flip = n.y < 0;
  const attr = new THREE.BufferAttribute(arr, 3);
  const wind = (ix: number[]): number[] => (flip ? ix.map((_, i, a) => a[i - (i % 3) + (2 - (i % 3))]!) : ix);
  // One smooth set of normals over the whole wing, shared by every band (the paint changes, not the shading).
  const whole = new THREE.BufferGeometry();
  whole.setAttribute('position', attr);
  whole.setIndex(wind([...bins.values()].flat()));
  whole.computeVertexNormals();
  const normal = whole.getAttribute('normal');
  for (const [c, ix] of bins) {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', attr);
    geo.setAttribute('normal', normal);
    geo.setIndex(wind(ix));
    M(geo, wingMat(c), parent);
  }
  // A fringe of loose scales off the outer margin: a ragged translucent hem, a little longer at the scallops.
  const fr: number[] = [];
  for (let j = 0; j < d.S; j++) {
    const u0 = j / d.S, u1 = (j + 1) / d.S;
    if (!isMargin(u0) && !isMargin(u1)) continue;
    const a = outline[j]!, b = outline[j + 1]!;
    const out = (q: THREE.Vector3, s: number): THREE.Vector3 => {
      const dx = q.x - rx, dz = q.z - rz, l = Math.hypot(dx, dz);
      const x = q.x + (dx / l) * s, z = q.z + (dz / l) * s;
      return V(x * k, yAt(x, z) - 0.004, z);
    };
    const ia = out(a, 0), ib = out(b, 0), oa = out(a, 0.03 + rng() * 0.03), ob = out(b, 0.025 + rng() * 0.035);
    const mid = out(a.clone().lerp(b, 0.5), 0.05 + rng() * 0.03);
    for (const v of [ia, ib, mid, ia, mid, oa, ib, ob, mid]) fr.push(v.x, v.y, v.z);
  }
  if (fr.length) {
    const fg = new THREE.BufferGeometry();
    fg.setAttribute('position', new THREE.Float32BufferAttribute(fr, 3));
    fg.computeVertexNormals();
    M(fg, fringeM, parent);
  }
  return {
    at: (i, j) => V(arr[grid[i - 1]![j]! * 3]!, arr[grid[i - 1]![j]! * 3 + 1]!, arr[grid[i - 1]![j]! * 3 + 2]!),
    yAt,
  };
};

/** A thin open three-sided rod from a to b (legs: no caps, nobody sees them). */
const bar = (parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, r1: number, r2: number, mat: THREE.Material): void => {
  const v = b.clone().sub(a);
  const m = M(new THREE.CylinderGeometry(r2, r1, v.length(), 3, 1, true), mat, parent);
  m.position.copy(a).addScaledVector(v, 0.5);
  m.quaternion.setFromUnitVectors(V(0, 1, 0), v.normalize());
};

/** Flat blades of fur, gathered into one geometry per colour. */
class Fur {
  private readonly by = new Map<string, number[]>();
  constructor(private readonly rng: Rng) {}
  /** A tapered blade from `base` along `dir`, its flat face toward `face` (3 faces; `cheap` = 1). */
  blade(c: string, base: THREE.Vector3, dir: THREE.Vector3, len: number, w: number, face = V(0, 1, 0), cheap = false): void {
    const a = this.by.get(c) ?? [];
    const dd = dir.clone().normalize();
    const n = dd.clone().cross(face).normalize();
    if (n.lengthSq() < 1e-6) n.set(1, 0, 0);
    const f = n.clone().cross(dd).normalize();
    const j = (this.rng() - 0.5) * w * 0.5;
    const at = (t: number, s: number, lift = 0): THREE.Vector3 => base.clone().addScaledVector(dd, len * t).addScaledVector(n, s).addScaledVector(f, lift);
    const tip = at(1, j, -0.15 * len);
    const push = (...vs: THREE.Vector3[]): void => {
      for (const v of vs) a.push(v.x, v.y, v.z);
    };
    if (cheap) push(at(0, -w / 2), at(0, w / 2), tip);
    else {
      const b0 = at(0, -w / 2), b1 = at(0, w / 2), m0 = at(0.5, -w * 0.42, 0.01), m1 = at(0.5, w * 0.42, 0.01);
      push(b0, b1, m1, b0, m1, m0, m0, m1, tip);
    }
    this.by.set(c, a);
  }
  flush(parent: THREE.Object3D): void {
    for (const [c, a] of this.by) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(a, 3));
      g.computeVertexNormals();
      M(g, std(c, { roughness: 0.95, side: THREE.DoubleSide, bumpScale: 0.03 }), parent);
    }
    this.by.clear();
  }
}

/* The palette: ash and bone, dusk, bruised violet-blue, ink. No warm tones but the faint ones the light gives. */
const ASH = '#a9acb6';
const BONE = '#c8cbd3';
const DUSK = '#62627a';
const FUR = '#4f4c60';
const BRUISE = '#4a4c7c';
const INK = '#1d1b29';
const LINE = '#33304c';

export const moth = (o: BaseOpts = {}): Model => {
  const c = begin(o, 0.62, 61);
  const { g, rng, R } = c;
  g.position.y = MOTH_HOVER;

  // The one self-lit material: cold dream light (eyes of the hindwings, eyeshine, sleep-dust).
  const dream = emis('#dfe8ff', '#93b0ff', 1.15, { roughness: 0.6, bumpScale: 0 });
  const fringeM = smoke('#c9c6d6', 0.42, { side: THREE.DoubleSide });
  const furBody = std('#6c6d82', { roughness: 0.95, bumpScale: 0.05 });
  const furDark = std('#3e3c52', { roughness: 0.95, bumpScale: 0.05 });
  const eyeM = std('#07060c', { roughness: 0.12, metalness: 0.55, bumpScale: 0 });

  const body = joint(g, V(0, Y0, 0), 'body');
  const head = joint(body.abs, V(0, Y0 + 0.01, 0.13), 'head');
  const ant = joint(head.abs, V(0, Y0 + 0.05, 0.19), 'antennae');
  const abd = joint(body.abs, V(0, Y0 - 0.01, -0.06), 'abdomen');
  const legs = joint(body.abs, V(0, Y0 - 0.06, 0.04), 'legs');
  const dust = joint(body.abs, V(0, Y0 - 0.06, -0.1), 'dust');
  const skel: Record<string, THREE.Object3D> = { body: body.p, head: head.p, antennae: ant.p, abdomen: abd.p };

  /* ---------- thorax, collar and mane ---------- */
  const thorax = M(new THREE.SphereGeometry(0.09, 7, 5), furBody, body.abs);
  thorax.scale.set(1.05, 0.85, 1.3);
  thorax.position.set(0, Y0, 0.03);
  const fur = new Fur(rng);
  // The collar: a shaggy ruff of pale scales standing forward round the neck (the moth's signature from above).
  for (let i = 0; i < 9; i++) {
    const a = (i / 8 - 0.5) * 2.7;
    fur.blade(i % 2 ? '#9497a6' : ASH, V(Math.sin(a) * 0.075, Y0 + 0.03 + Math.cos(a) * 0.02, 0.1), V(Math.sin(a) * 0.9, 0.22 + R() * 0.15, 0.7), 0.085 + rng() * 0.025, 0.065, V(Math.sin(a), 1, 0));
  }
  // The mane down the back: ash streaked with dusk, raking back over the wing roots.
  for (let row = 0; row < 3; row++) {
    for (let i = 0; i < 4; i++) {
      const x = (i / 3 - 0.5) * 0.12;
      fur.blade(row === 1 && (i === 1 || i === 2) ? DUSK : i % 2 ? ASH : '#868a99', V(x, Y0 + 0.07 - row * 0.01, 0.08 - row * 0.06), V(x * 2.5, 0.1, -1), 0.1 + rng() * 0.03, 0.065, V(0, 1, 0.2));
    }
  }
  // Tegulae: long flaps of scales over the wing roots.
  for (const k of [-1, 1]) {
    for (let i = 0; i < 3; i++) fur.blade(i === 1 ? DUSK : '#868a99', V(k * 0.07, Y0 + 0.04, 0.06 - i * 0.04), V(k * 0.55, -0.05, -1), 0.13, 0.05, V(k * 0.4, 1, 0));
  }
  fur.flush(body.abs);

  /* ---------- abdomen: furred, ringed, drooping ---------- */
  const prof: [number, number][] = [[0.0, -0.36], [0.026, -0.345], [0.045, -0.3], [0.056, -0.25], [0.05, -0.225], [0.064, -0.2], [0.058, -0.17], [0.07, -0.14], [0.064, -0.11], [0.072, -0.08], [0.062, -0.03], [0.045, 0.0]];
  const belly = M(new THREE.LatheGeometry(prof.map(([x, y]) => new THREE.Vector2(x, y)), 7), furBody, abd.abs);
  belly.rotation.x = Math.PI / 2 - 0.16;
  belly.scale.set(1.1, 1, 0.85);
  belly.position.set(0, Y0 - 0.01, -0.06);
  const af = new Fur(rng);
  // Pale fringes at the segment rings and a dusk streak down the back; a tuft at the tip.
  // Each ring a fan of soft scales over the top half, lying back along the abdomen as it droops.
  for (const [z, r, n] of [[-0.09, 0.07, 0], [-0.16, 0.068, 1], [-0.23, 0.06, 2], [-0.3, 0.045, 3]] as const) {
    const y = Y0 - 0.01 + (z + 0.06) * 0.16;
    for (let i = 0; i < 5; i++) {
      const a = (i / 4 - 0.5) * 2.4;
      const col = n % 2 ? (i === 2 ? FUR : '#5c5c72') : i === 2 ? DUSK : '#8a8d9c';
      af.blade(col, V(Math.sin(a) * r * 1.05, y + Math.cos(a) * r * 0.82, z), V(Math.sin(a) * 0.35, -0.16 - (1 - Math.cos(a)) * 0.3, -1), 0.085, 0.06, V(Math.sin(a), Math.cos(a), 0));
    }
  }
  for (let i = 0; i < 4; i++) {
    const a = (i / 3 - 0.5) * 1.6;
    af.blade(i % 2 ? '#7d8090' : FUR, V(Math.sin(a) * 0.02, Y0 - 0.07, -0.37), V(Math.sin(a) * 0.6, -0.3, -1), 0.075, 0.05);
  }
  af.flush(abd.abs);

  /* ---------- head: ink eyes with a pale shine, a forelock, palps ---------- */
  const skull = M(new THREE.SphereGeometry(0.052, 6, 4), furDark, head.abs);
  skull.position.set(0, Y0 + 0.005, 0.17);
  skull.scale.set(1.1, 0.95, 0.9);
  for (const k of [-1, 1]) {
    const eye = M(new THREE.SphereGeometry(0.036, 6, 4), eyeM, head.abs);
    eye.position.set(k * 0.042, Y0 + 0.004, 0.19);
    // The shine deep in the eye (moths' eyes catch the light).
    const shine = M(new THREE.SphereGeometry(0.01, 4, 2), dream, head.abs);
    shine.position.set(k * 0.06, Y0 + 0.014, 0.205);
    glowSprite(head.abs, '#8fa9ff', 0.07, V(k * 0.062, Y0 + 0.014, 0.215), 0.45);
  }
  const hf = new Fur(rng);
  for (let i = 0; i < 4; i++) {
    const x = (i - 1.5) * 0.02;
    hf.blade(i % 2 ? ASH : BONE, V(x, Y0 + 0.045, 0.15), V(x * 2, 0.5, 1), 0.06, 0.03, V(0, 1, -0.4), true);
  }
  for (const k of [-1, 1]) hf.blade(FUR, V(k * 0.015, Y0 - 0.03, 0.2), V(k * 0.2, -0.6, 1), 0.06, 0.022, V(k, 0, 0), true);
  hf.flush(head.abs);

  /* ---------- antennae: plumed like ferns ---------- */
  const shaftM = std('#3b3648', { roughness: 0.8, bumpScale: 0 });
  const af2 = new Fur(rng);
  for (const k of [-1, 1]) {
    const pts = [V(k * 0.02, Y0 + 0.045, 0.19), V(k * 0.07, Y0 + 0.1, 0.26), V(k * 0.14, Y0 + 0.13, 0.33), V(k * 0.22, Y0 + 0.12, 0.38)];
    const cv = new THREE.CatmullRomCurve3(pts);
    M(new THREE.TubeGeometry(cv, 4, 0.006, 3), shaftM, ant.abs);
    // The plumes: combs on both sides, widest midway, raked forward, lying flat so they read from above.
    const N = 8;
    for (let i = 1; i < N; i++) {
      const s = i / N;
      const p = cv.getPointAt(s);
      const tg = cv.getTangentAt(s);
      const side = tg.clone().cross(V(0, 1, 0)).normalize();
      const len = 0.075 * Math.sin(Math.PI * (0.15 + s * 0.85)) + 0.012;
      for (const sd of [-1, 1]) {
        const dir = side.clone().multiplyScalar(sd).addScaledVector(tg, 0.9).add(V(0, 0.15, 0));
        af2.blade(sd < 0 ? '#9497a8' : '#b0b3bf', p, dir, len, 0.022, V(0, 1, 0), true);
      }
    }
  }
  af2.flush(ant.abs);

  /* ---------- wings ---------- */
  // Forewing: a long blade with a hooked apex, banded root to margin: dusky fur, pale ash, a dark wavy line,
  // a band of bruised violet, a pale blaze at the apex and a checker of ink on the scalloped margin.
  const FW: WingDef = {
    root: [0.05, 0.04], y: Y0 + 0.03, droop: 0.07, S: 12, RN: 6, margin: [0.42, 0.97], tears: [9],
    ctrl: [[0.09, 0.1], [0.3, 0.2], [0.55, 0.23], [0.76, 0.21], [0.92, 0.14], [0.89, 0.0], [0.79, -0.14], [0.63, -0.27], [0.45, -0.33], [0.27, -0.28], [0.11, -0.11]],
    paint: (i, j, u) => {
      const apex = u > 0.27 && u < 0.42;
      if (i === 0) return FUR;
      if (i === 1) return DUSK;
      if (i === 2) return j === 5 ? INK : ASH;
      if (i === 3) return j % 4 === 3 ? '#5b5672' : LINE;
      if (i === 4) return apex ? BONE : BRUISE;
      return apex ? ASH : j % 3 === 0 ? INK : '#8a8aa2';
    },
  };
  // Hindwing: rounded, bruised violet-blue darkening to a pale hem; its eye is laid on top.
  const HW: WingDef = {
    root: [0.045, -0.02], y: Y0 + 0.015, droop: 0.06, S: 12, RN: 6, margin: [0.25, 0.92], tears: [6],
    ctrl: [[0.1, 0.0], [0.3, -0.06], [0.49, -0.14], [0.59, -0.29], [0.57, -0.45], [0.47, -0.57], [0.31, -0.61], [0.18, -0.52], [0.09, -0.33], [0.05, -0.12]],
    paint: (i, j, u) => {
      const lee = u < 0.2;
      if (i === 0) return FUR;
      if (i === 1) return DUSK;
      if (i === 2 || i === 3) return lee ? '#7d7a90' : BRUISE;
      if (i === 4) return lee ? ASH : LINE;
      return lee ? BONE : j % 3 === 1 ? INK : '#9293ad';
    },
  };
  // The veins: thin dark ribs fanning out over each wing.
  const vein = std('#2a2638', { roughness: 0.9, side: THREE.DoubleSide, bumpScale: 0 });
  const veins = (w: BuiltWing, d: WingDef, parent: THREE.Object3D, spokes: number[]): void => {
    const vp: number[] = [];
    for (const j of spokes) {
      let prev: THREE.Vector3 | null = null;
      for (let i = 1; i <= d.RN - 1; i++) {
        const p = w.at(i, j);
        p.y += 0.003;
        if (prev) {
          const dx = p.x - prev.x, dz = p.z - prev.z, l = Math.hypot(dx, dz);
          const wv = 0.007 * (1 - i / d.RN) + 0.003;
          const nx = (-dz / l) * wv, nz = (dx / l) * wv;
          const a0 = V(prev.x - nx, prev.y, prev.z - nz), a1 = V(prev.x + nx, prev.y, prev.z + nz), b0 = V(p.x - nx, p.y, p.z - nz), b1 = V(p.x + nx, p.y, p.z + nz);
          for (const v of [a0, a1, b1, a0, b1, b0]) vp.push(v.x, v.y, v.z);
        }
        prev = p;
      }
    }
    const vg = new THREE.BufferGeometry();
    vg.setAttribute('position', new THREE.Float32BufferAttribute(vp, 3));
    vg.computeVertexNormals();
    M(vg, vein, parent);
  };
  const eyeRing = std('#15121f', { roughness: 0.7, side: THREE.DoubleSide, bumpScale: 0 });
  const eyeIris = std('#4f5d92', { roughness: 0.6, side: THREE.DoubleSide, bumpScale: 0 });
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const fore = joint(body.abs, V(k * FW.root[0], FW.y, FW.root[1]), `fore${s}`);
    const hind = joint(body.abs, V(k * HW.root[0], HW.y, HW.root[1]), `hind${s}`);
    skel[`fore${s}`] = fore.p;
    skel[`hind${s}`] = hind.p;
    const fw = buildWing(FW, k, fore.abs, rng, fringeM);
    veins(fw, FW, fore.abs, [2, 4, 6, 8]);
    const hw = buildWing(HW, k, hind.abs, rng, fringeM);
    veins(hw, HW, hind.abs, [4, 9]);
    // The eye of the hindwing: an ink ring, a pale iris and a pupil of dream light, glowing.
    const ex = 0.38, ez = -0.43, ey = hw.yAt(ex, ez);
    const disc = (r: number, m: THREE.Material, dy: number, sx = 1): void => {
      const d = M(new THREE.CircleGeometry(r, 12), m, hind.abs);
      d.rotation.x = -Math.PI / 2;
      d.rotation.z = -k * 0.5;
      d.scale.set(sx, 1, 1);
      d.position.set(k * ex, ey + dy, ez);
    };
    disc(0.105, eyeRing, 0.005, 1.15);
    disc(0.074, eyeIris, 0.007, 1.1);
    disc(0.04, dream, 0.009);
    glowSprite(hind.abs, '#9fbaff', 0.3, V(k * ex, ey + 0.03, ez), 0.5);
    // A smaller false eye on the forewing's apex band, unlit.
    const fe = M(new THREE.CircleGeometry(0.032, 8), eyeRing, fore.abs);
    fe.rotation.x = -Math.PI / 2;
    fe.position.set(k * 0.6, fw.yAt(0.6, -0.04) + 0.006, -0.04);
  }

  /* ---------- legs, hanging ---------- */
  skel.legs = legs.p;
  const legM = std('#3d384a', { roughness: 0.85, bumpScale: 0.02 });
  const tarsus = std('#868a99', { roughness: 0.85, bumpScale: 0.02 });
  for (const k of [-1, 1]) {
    for (let i = 0; i < 3; i++) {
      const z = 0.08 - i * 0.05;
      const hip = V(k * 0.035, Y0 - 0.05, z), knee = V(k * (0.1 + i * 0.01), Y0 - 0.09, z + 0.02 - i * 0.03), foot = V(k * (0.11 + i * 0.015), Y0 - 0.19, z - 0.02 - i * 0.04);
      bar(legs.abs, hip, knee, 0.011, 0.008, legM);
      bar(legs.abs, knee, foot, 0.007, 0.003, tarsus);
    }
  }

  /* ---------- the sleep-dust: motes and haze shed off the wings ---------- */
  skel.dust = dust.p;
  // Motes of dream light drifting down off the wings and a haze of glow over them; it hangs under the wings and trails behind. Pivot under the body: the clips
  // scale it out into a cloud.
  const P0 = V(0, Y0 - 0.06, -0.1);
  const mote = new THREE.TetrahedronGeometry(0.009);
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2 + R() * 0.5;
    const r = 0.15 + rng() * 0.55;
    const m = M(mote, dream, dust.abs);
    m.position.set(Math.cos(a) * r, P0.y - 0.02 - rng() * 0.2, P0.z + Math.sin(a) * r * 0.55 - 0.06);
    m.rotation.set(rng() * 3, rng() * 3, rng() * 3);
    m.scale.setScalar(0.6 + rng() * 0.8);
  }
  for (const [x, y, z, sz] of [[0.42, -0.08, -0.25, 0.4], [-0.45, -0.07, -0.22, 0.38], [0, -0.2, -0.42, 0.36], [0.24, -0.24, 0.06, 0.28], [-0.26, -0.22, 0.04, 0.28], [0.62, -0.16, -0.05, 0.26], [-0.6, -0.15, -0.08, 0.26], [0, -0.3, -0.12, 0.32], [0.3, -0.3, -0.5, 0.26], [-0.32, -0.28, -0.48, 0.26]] as const) {
    glowSprite(dust.abs, sz > 0.3 ? '#93adff' : '#c4d2ff', sz, V(x, Y0 + y, z), 0.3);
  }

  return done(c, MOTH_HOVER + Y0 + 0.2, undefined, undefined, bonesOf(c, skel));
};
